// sessionConfigStore — reads/writes the loaded character's `convorcherSession`
// Event-Graph node fields (character / features / story / fastSentences). The
// generalization of sessionStoryStore (ENG-853 P2), which handled only `story`:
// the Brain workspace's session panel edits all four levers through one path,
// so the workspace and the node can never disagree about what the next session
// opens with.
//
// Node discovery goes through sessionCharacter.findConvorcherSessionNode — the
// ENG-907 shared finder that also looks one level into a `graphGroup`'s
// innerNodes. That matters here: `convorcherSession` is a GLOBAL_UNIQUE type
// explicitly allowed inside a group, and the old story-only patcher searched
// the top level only, so a group-nested session node read as "no session
// declared" and the selector disabled itself for a character that talks fine.
// The patcher below patches the group-nested copy too.
//
// Persistence is the same cloud save the BlueprintEditor uses (collab-OFF
// path — when collab co-edit is on the editor owns the Yjs write and callers
// disable themselves with a hint, exactly like StorySelector). Deps are
// injected so the logic is unit-testable without React or network.

import { findConvorcherSessionNode } from './sessionCharacter.js'
import { mergeBlueprintForSave } from './blueprintSaveGuard.js'

/** The only node-data keys this store may write. Anything else is a caller
 *  bug — thrown, not silently merged into the node. */
export const SESSION_CONFIG_FIELDS = Object.freeze(['character', 'features', 'story', 'fastSentences'])

function eventNodesOf(blueprint) {
  return blueprint?.events?.eventNodes ?? blueprint?.eventNodes ?? []
}

const str = (v) => (v == null ? '' : String(v).trim())

// The ConvorcherClient.setSessionConfig argument a session node's data resolves
// to — the same normalization EventRuntime's `convorcherSession` exec case
// applies (empty → null so an unset field defers to the page-URL params;
// fastSentences kept verbatim because '' means unset while '0' is meaningful).
export function sessionConfigFromNode(node) {
  const d = node?.data ?? {}
  return {
    character: str(d.character) || null,
    features: str(d.features) || null,
    story: str(d.story) || null,
    fastSentences: d.fastSentences ?? null,
  }
}

/**
 * The config to hand the LIVE ConvorcherClient after a session-panel write, or
 * null when the character has no session node (nothing to apply).
 *
 * The character falls back to the RENDERED slug exactly as
 * sessionCharacter.resolveSessionConfig does at boot, and that fallback is the
 * whole point: CLEARING the brain writes character:'' to the node, and a
 * caller that skipped the apply on a falsy character left the running client
 * conversing as the brain the user had just removed, with a clean save badge
 * over the divergence. Pushing a bare null instead would be the other half of
 * the same bug — boot fills that hole with the rendered character, so the live
 * client would end up with a persona boot never gives it.
 *
 * story/features/fastSentences always come from the node: the panel just wrote
 * them there, and the node is their only source.
 */
export function liveSessionConfigFor(nodes, renderedSlug) {
  const node = findConvorcherSessionNode(nodes ?? [])
  if (!node) return null
  const cfg = sessionConfigFromNode(node)
  return cfg.character ? cfg : { ...cfg, character: str(renderedSlug) || null }
}

function checkFields(fields) {
  const keys = Object.keys(fields ?? {})
  if (keys.length === 0) throw new Error('setSessionFields: no fields given')
  for (const k of keys) {
    if (!SESSION_CONFIG_FIELDS.includes(k)) {
      throw new Error(`setSessionFields: unknown session field "${k}"`)
    }
  }
  return fields
}

// Pure: return a NEW blueprint with the convorcherSession node's fields merged.
// Patches the node wherever it lives — top level or one level inside a
// graphGroup. Throws when the blueprint has no such node (callers must guard
// first, same contract as the story-only patcher this replaces).
export function patchSessionFieldsInBlueprint(blueprint, fields) {
  const clean = checkFields(fields)
  const nodes = eventNodesOf(blueprint)
  if (!findConvorcherSessionNode(nodes)) throw new Error('no convorcherSession node in blueprint')

  const patchNode = (n) => ({ ...n, data: { ...n.data, ...clean } })
  const nextNodes = nodes.map((n) => {
    if (n?.type === 'convorcherSession') return patchNode(n)
    if (n?.type === 'graphGroup' && (n.data?.innerNodes ?? []).some((i) => i?.type === 'convorcherSession')) {
      return {
        ...n,
        data: {
          ...n.data,
          innerNodes: n.data.innerNodes.map((i) => (i?.type === 'convorcherSession' ? patchNode(i) : i)),
        },
      }
    }
    return n
  })

  if (blueprint.events?.eventNodes) {
    return { ...blueprint, events: { ...blueprint.events, eventNodes: nextNodes } }
  }
  return { ...blueprint, eventNodes: nextNodes }
}

// Load → patch → save → mirror into live runtime state → re-apply the session.
// Same injected deps as setSessionStory; `fields` is the delta.
//
// The server copy is ALWAYS fetched first: the singleton blueprint save
// REPLACES the whole document, so a graph missing from the payload is a graph
// deleted on the server. The old story-only flow saved a bare `{ events }`
// document, which the server refuses — saveBlueprintToDB logged a warning,
// returned false, and the selector showed a clean badge over a write that
// never landed. So: the payload is a COMPLETE document assembled by
// mergeBlueprintForSave from this browser's own graph caches, with the server's
// copy filling any graph this browser doesn't hold (never the server's copy
// where a local one exists — see the comment at the assembly), and a
// refused/failed save THROWS so the caller's badge goes red instead of lying.
export async function setSessionFields({ charId, fields, eventGraphState, readLocalGraphs, loadBlueprint, saveBlueprint, applySession }) {
  const clean = checkFields(fields)
  // Required, not defaulted: without it the payload is assembled from the
  // SINGLETON blueprint, and saveBlueprint routes each graph to whichever
  // library row is open (see below) — so a missing dep is a silent clobber,
  // exactly the class of failure a default would hide.
  if (typeof readLocalGraphs !== 'function') {
    throw new Error('setSessionFields: readLocalGraphs is required (loadBlueprint.js readBlueprintCaches)')
  }

  // A 404 is evidence (no blueprint row yet → everything server-empty); any
  // other failure rethrows — a network blip must not open the destructive
  // path (blueprintSaveGuard.js).
  let server
  try {
    server = await loadBlueprint(charId)
  } catch (e) {
    if (e?.status === 404) server = {}
    else throw e
  }
  if (server == null || typeof server !== 'object') server = {}

  // The payload must be the graphs THIS BROWSER holds, assembled exactly the
  // way BlueprintEditor's Save-to-Cloud assembles them (mergeBlueprintForSave:
  // local wins, the server copy only fills holes, nothing is ever fabricated
  // as `{}`). Two reasons it can't be the server document instead:
  //
  //  • saveBlueprintToDB PATCHes each graph into the blueprint LIBRARY ROW the
  //    editor has open for it (ENG-1030). Sending the singleton's body/face
  //    there writes one blueprint's content over another's — a row-level mixup
  //    the server cannot catch for us, because each PATCH is well-formed and
  //    non-destructive on its own; only the caller knows it addressed the
  //    wrong row.
  //  • the events graph is more than nodes+edges: `variables` (what body/face
  //    read via getVariable) and `functions` (custom sub-graphs) live there
  //    too, and the save REPLACES the graph, so a payload without them deletes
  //    them.
  const cached = readLocalGraphs(charId) ?? {}

  // Events prefer the LIVE graph — the editor shares node objects, so unsaved
  // event-graph edits aren't clobbered — but variables/functions are only ever
  // held by the cache (eventGraphState has no `variables` at all), so they're
  // carried across rather than dropped.
  const liveEvents = eventGraphState?.nodes?.length
    ? {
        eventNodes: eventGraphState.nodes,
        eventEdges: eventGraphState.edges ?? [],
        variables: cached.events?.variables ?? [],
        functions: eventGraphState.functions ?? cached.events?.functions ?? {},
      }
    : null

  const local = {
    body: cached.body ?? null,
    face: cached.face ?? null,
    events: liveEvents ?? cached.events ?? null,
  }
  const patched = patchSessionFieldsInBlueprint(mergeBlueprintForSave(local, server), clean)
  const ok = await saveBlueprint(charId, patched)
  if (ok === false) {
    throw new Error('blueprint save failed — the session change was not persisted (see console)')
  }

  // Mirror into live runtime state so the running/next session picks it up now.
  const liveNode = findConvorcherSessionNode(eventGraphState?.nodes ?? [])
  if (liveNode) liveNode.data = { ...liveNode.data, ...clean }
  applySession?.()
  return { ok: true }
}
